import React, { ChangeEvent, useMemo } from 'react';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext } from 'react-hook-form';
import { AutocompleteField } from 'components/form';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Checkbox, FilterOptionsState, Radio, Typography } from '@mui/material';
import { LenderProfiles } from '@trnsact/trnsact-shared-types/src/generated';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { LenderOptions } from '../../../../types';
import { FinanceProgramForm } from '../../../../schema';
import { lenderTypesSettings } from '../../../../../../constants/common';

interface Props {
  isMultipleLenders?: boolean;
  lendersOptions: LenderProfiles[];
  isLendersSelectDisabled?: boolean;
}

const lenderTypesSettingsByValue = _.keyBy(lenderTypesSettings, 'code');

export const LendersAutocompleteField = ({
  lendersOptions,
  isMultipleLenders = true,
  isLendersSelectDisabled = false,
}: Props) => {
  const classes = useStyles();

  const { control, setValue } = useFormContext<FinanceProgramForm>();

  const lendersOptionsAdaptedOptions = useMemo<LenderOptions[]>(
    () => lendersOptions.map(option => ({ ...option, value: option.id!, label: (option as any)?.lenderName ?? '' })),
    [lendersOptions]
  );

  const customFilter = (options: LenderOptions[], state: FilterOptionsState<LenderOptions>) =>
    options.filter(
      option =>
        option.label?.toLowerCase()?.includes(state?.inputValue?.toLowerCase()) ||
        (option as any)?.submissionMethod?.toLowerCase()?.includes(state?.inputValue?.toLowerCase())
    );

  const handleChange = (_: ChangeEvent<{}>, value: LenderOptions[]) => {
    const nextLendersValue = isMultipleLenders ? value : [value[value.length - 1]].filter(Boolean);

    setValue('program.first.details.lenders', nextLendersValue);
  };

  return (
    <AutocompleteField<LenderOptions, true, FinanceProgramForm>
      multiple
      control={control}
      disableCloseOnSelect
      onChange={handleChange}
      filterOptions={customFilter}
      name="program.first.details.lenders"
      options={lendersOptionsAdaptedOptions}
      disabled={isLendersSelectDisabled || !lendersOptions?.length}
      isOptionEqualToValue={(option, value) => value && option.value === value?.value}
      textFieldProps={{
        label: isMultipleLenders ? 'Select Lender(s)' : 'Select Lender',
        placeholder: 'Search by name or submission method...',
      }}
      renderOption={(props, option, state, ownerState) => {
        const { key, ...optionProps } = props;
        const method = (option as any)?.submissionMethod;
        const lenderTypeSetting = option.lenderTypeCode && lenderTypesSettingsByValue?.[option.lenderTypeCode];

        return (
          <li key={key} {...optionProps} className={classes.listItem}>
            {isMultipleLenders ? (
              <Checkbox
                checked={state.selected}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              />
            ) : (
              <Radio size="small" checked={state.selected} />
            )}
            <Box className={classes.content}>
              <Box className={classes.labelContainer}>
                <Typography component="span" variant="body1">
                  {option.label}
                </Typography>

                {lenderTypeSetting && (
                  <Box className={classes.specificLabelPart}>
                    <Typography style={{ color: lenderTypeSetting?.color ?? '' }}>
                      {lenderTypeSetting?.letter ?? ''}
                    </Typography>

                    <Typography> {lenderTypeSetting?.title ?? ''}</Typography>
                  </Box>
                )}
              </Box>

              {method && (
                <Typography component="span" variant="caption" color="textSecondary">
                  {method}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
    />
  );
};

const useStyles = makeStyles({
  listItem: {
    gap: '6px',
    display: 'flex',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
  },
  labelContainer: {
    gap: '6px',
    display: 'flex',
  },
  specificLabelPart: {
    gap: '4px',
    display: 'flex',
  },
});
